/**
 * Production media primitives (Phase 11.5.7 — literal realDemo clone). Media mirrors the
 * reference `common/media/landing-media`: a `.sbp-media` wrapper whose img/video/iframe fills
 * it with `object-fit: cover` (foundation.css). Image is a bare framed <img> for card
 * thumbnails (reference `CustomImage`). Assets arrive already resolved; these never resolve ids.
 */
import type { ResolvedAsset } from '../../runtime/types'
import type { MediaKind } from '../../schema'
import { Icon } from './icon'
import { cx } from './utils'

/** Extract a YouTube video id from a full url, embed url, or bare id. */
function youtubeId(url: string): string {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/)
  return match ? match[1] : url
}

function Placeholder() {
  return (
    <span className="sbp-media__placeholder">
      <Icon name="image" size={40} />
    </span>
  )
}

interface ImageProps {
  readonly asset: ResolvedAsset | null
  readonly alt?: string
  readonly className?: string
}

/** A bare image (reference CustomImage) — sized by the consuming section/card CSS. */
export function Image({ asset, alt, className }: ImageProps) {
  if (!asset || asset.isPlaceholder) {
    return <span className={cx('sbp-img', 'sbp-media__placeholder', className)}><Icon name="image" size={40} /></span>
  }
  return <img className={cx('sbp-img', className)} src={asset.url} alt={alt ?? asset.alt ?? ''} loading="lazy" />
}

interface MediaProps {
  readonly asset: ResolvedAsset | null
  readonly kind?: MediaKind
  readonly alt?: string
  readonly className?: string
}

/** Reference `.landing-media` wrapper that dispatches on media kind (fills its parent). */
export function Media({ asset, kind = 'image', alt, className }: MediaProps) {
  return (
    <div className={cx('sbp-media', className)}>
      {!asset || asset.isPlaceholder ? (
        <Placeholder />
      ) : kind === 'video' ? (
        <video src={asset.url} muted loop playsInline autoPlay aria-label={alt ?? asset.alt ?? undefined} />
      ) : kind === 'youtube' ? (
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId(asset.url)}`}
          title={alt ?? asset.alt ?? 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <img src={asset.url} alt={alt ?? asset.alt ?? ''} loading="lazy" />
      )}
    </div>
  )
}
